import Icon, { ICON_SIZES } from "@/components/library/Icon";
import type { NodeDefinitionItem } from "@/components/workflows/utils/helpers/workflow-node-registry";

const ICON_SIZE_REM: Record<ICON_SIZES, string> = {
  [ICON_SIZES.XXS]: "0.625rem",
  [ICON_SIZES.XS]: "0.75rem",
  [ICON_SIZES.SM]: "0.875rem",
  [ICON_SIZES.BASE]: "1rem",
  [ICON_SIZES.LG]: "1.25rem",
  [ICON_SIZES.XL]: "1.5rem",
  [ICON_SIZES.XXL]: "2rem",
};

interface NodeDefinitionIconProps {
  node: Pick<NodeDefinitionItem, "icon" | "iconUrl">;
  size?: ICON_SIZES;
  className?: string;
}

/**
 * Renders a node's icon, preferring a brand SVG (`iconUrl`) when set,
 * otherwise falling back to the FontAwesome `icon`. Keeps brand assets
 * (Google Drive, Slack, etc.) visually consistent with the FA-sized icons
 * elsewhere in the workflow editor (NodePicker, suggestion menus, headers).
 */
export function NodeDefinitionIcon({
  node,
  size = ICON_SIZES.SM,
  className,
}: NodeDefinitionIconProps) {
  if (node.iconUrl) {
    const dimension = ICON_SIZE_REM[size];
    // Decorative: every call site renders the node label adjacent to the
    // icon, and the FA fallback below is itself `aria-hidden`. Empty `alt`
    // keeps assistive tech from announcing the same label twice.
    return (
      <img
        src={node.iconUrl}
        alt=""
        aria-hidden
        style={{ width: dimension, height: dimension }}
        className={className}
      />
    );
  }
  return <Icon icon={node.icon} size={size} className={className} />;
}
